import type { MseProduct } from '@/types/home/product';

export type Languages = 'en' | 'cn' | 'tw' | 'jp';
// Backward-compatible alias to avoid touching call sites that still reference `languages`.
type languages = Languages;
export type ExampleItem = {
  preText?: string;
  text: string;
};

export type FileExampleItem = {
  text: string;
  imgLink: string;
  fileLink: string;
};

export const EXAMPLES: Partial<Record<languages, Partial<Record<MseProduct, ExampleItem[]>>>> = {
  en: {
    FIND_SOLUTIONS: [
      {
        text: 'How to improve the adhesion and durability of waterborne polyurethane coatings in high humidity environments, while reducing the emission of volatile organic compounds (VOCs)?',
      },
      {
        text: 'How to improve the UVA protection effect of zinc oxide sunscreen without affecting stability in high temperature and high humidity environments, while avoiding greasy residue?',
      },
      {
        text: 'How to improve the oxidation and corrosion resistance of nickel-based alloys at temperatures above 1000°C?',
      },
    ],
    FORMULATION: [
      {
        text: 'Develop a ceramide-based cream targeted at dry, compromised skin. The cream should not leave a greasy finish and needs to achieve ≥25% TEWL reduction within 2 h.',
      },
      {
        text: 'Formulate a structural adhesive for cell-to-pack bonding in EV batteries. It must achieve a thermal conductivity of >2.0 W/m·K using non-abrasive fillers and possess a dielectric strength of >15 kV/mm. It must maintain a thixotropic index suitable for high-speed robotic bead application without slumping on vertical surfaces.',
      },
      {
        text: 'Create a synthesis protocol for the chemical recycling of rigid PU foam waste (e.g., from end-of-life refrigerators) using a split-phase glycolysis approach.',
      },
    ],
    FORMULATION_LEGACY: [
      {
        text: 'I want to develop a sunscreen with SPF 50 and water resistance. I want to include zinc oxide and glycerin.',
      },
      {
        text: 'I need a rubber formulation for explosion-proof tires, and I want to include carbon black, sulfur and stearic acid.',
      },
    ],
    REVERSE_ENGINEERING: [
      {
        text: 'Reconstruct and analyze the Si/SiGe multilayer stacked epitaxy process used in GAA/nanosheet transistors.',
      },
      {
        text: 'Deconstruct the material system, curing mechanism, and coating-lamination process of solvent-free two-component polyurethane laminating adhesives for high-barrier flexible packaging.',
      },
      {
        text: 'Reconstruct the key technical pathway of automotive sulfide-based all-solid-state batteries, from material and interface design to cell manufacturing.',
      },
    ],
  },
  cn: {
    FIND_SOLUTIONS: [
      {
        text: '如何在高湿度环境下改善水性聚氨酯涂料的附着力和耐久性，同时减少挥发性有机化合物(VOC)的排放？',
      },
      {
        text: '如何在不影响高温高湿环境下稳定性的前提下，改善氧化锌防晒霜的UVA防护效果，同时避免油腻残留？',
      },
      {
        text: '如何提高镍基合金在1000°C以上温度下的抗氧化和抗腐蚀能力？',
      },
    ],
    FORMULATION: [
      {
        text: '开发一款针对干燥、受损肌肤的神经酰胺面霜。面霜使用后不应有油腻感，并需在2小时内使经皮水分流失（TEWL）降低≥25%。',
      },
      {
        text: '开发一种用于电动汽车电池 CTP（无模组）粘接的结构胶。需使用非磨蚀性填料实现 >2.0 W/m·K 的导热率，且介电强度 >15 kV/mm。需保持良好的触变指数，以适应高速机器人涂胶且在垂直面上不流挂。',
      },
      {
        text: '制定针对废旧冰箱等硬质聚氨酯（PU）泡沫废料的化学回收合成方案，采用分相醇解法（Split-phase glycolysis）。',
      },
    ],
    FORMULATION_LEGACY: [
      {
        text: '我想开发一款具有 SPF 50 和防水效果的防晒霜，希望加入氧化锌和甘油。',
      },
      {
        text: '我需要一款防爆轮胎用橡胶配方，希望包含炭黑、硫磺和硬脂酸。',
      },
    ],
    REVERSE_ENGINEERING: [
      {
        text: '还原并分析用于 GAA / 纳米片晶体管的 Si/SiGe 多层堆叠外延工艺流程。',
      },
      {
        text: '拆解高阻隔软包装用无溶剂型双组分聚氨酯复合胶的材料体系、固化机理与涂布复合工艺。',
      },
      {
        text: '重构车用硫化物全固态电池从材料与界面设计到电芯制造的关键技术路径。',
      },
    ],
  },
  tw: {
    FIND_SOLUTIONS: [
      {
        text: '如何在高度濕環境下改善水性聚氨酯塗料的附著力和耐久性，同時減少揮發性有機化合物(VOC)的排放？',
      },
      {
        text: '如何在不影響高溫高濕環境下穩定性的前提下，改善氧化鋅防曬霜的UVA防護效果，同時避免油膩殘留？',
      },
      {
        text: '如何提高鎳基合金在1000°C以上溫度下的抗氧化和抗腐蝕能力？',
      },
    ],
    FORMULATION: [
      {
        text: '開發一款針對乾燥、受損肌膚的神經醯胺面霜。面霜使用後不應有油膩感，並需在2小時內使經皮水分流失（TEWL）降低≥25%。',
      },
      {
        text: '開發一種用於電動汽車電池 CTP（無模組）粘接的結構膠。需使用非磨蝕性填料實現 >2.0 W/m·K 的導熱率，且介電強度 >15 kV/mm。需保持良好的觸變指數，以適應高速機器人塗膠且在垂直面上不流掛。',
      },
      {
        text: '針對廢舊冰箱等硬質聚氨酯（PU）泡沫廢料，制定採用分相醇解法（Split-phase glycolysis）的化學回收合成方案。',
      },
    ],
    REVERSE_ENGINEERING: [
      {
        text: '還原並分析用於 GAA / 奈米片電晶體的 Si/SiGe 多層堆疊磊晶製程。',
      },
      {
        text: '拆解高阻隔軟包裝用無溶劑型雙組分聚氨酯複合膠的材料體系、固化機理與塗佈複合製程。',
      },
      {
        text: '重構車用硫化物全固態電池從材料與介面設計到電芯製造的關鍵技術路徑。',
      },
    ],
  },
  jp: {
     FIND_SOLUTIONS: [
      {
        text: 'How to improve the adhesion and durability of waterborne polyurethane coatings in high humidity environments, while reducing the emission of volatile organic compounds (VOCs)?',
      },
      {
        text: 'How to improve the UVA protection effect of zinc oxide sunscreen without affecting stability in high temperature and high humidity environments, while avoiding greasy residue?',
      },
      {
        text: 'How to improve the oxidation and corrosion resistance of nickel-based alloys at temperatures above 1000°C?',
      },
    ],
    FORMULATION: [
      {
        text: '乾燥し、バリア機能が低下した肌向けのセラミド配合クリームを開発する。べたつきのない仕上がりとし、2時間以内にTEWL（経表皮水分蒸散量）を25％以上低減すること。',
      },
      {
        text: 'EVバッテリーのセル・トゥ・パック（CTP）接合用構造用接着剤の処方を策定。非研磨性フィラーを用いて熱伝導率 2.0 W/m·K 以上、絶縁破壊強度 15 kV/mm 以上を実現し、垂直面で垂れのない高速ロボット塗布に適したチクソ性を維持すること。',
      },
      {
        text: '廃冷蔵庫等の硬質ポリウレタンフォーム廃棄物を対象とした、スプリットフェーズ（分相）グリコリス法による化学リサイクルの合成プロトコルを作成。',
      },
    ],
    REVERSE_ENGINEERING: [
      {
        text: 'GAA / ナノシートトランジスタ向けSi/SiGe多層積層エピタキシャル成長プロセスを復元・分析する。',
      },
      {
        text: 'ハイバリア軟包装向け無溶剤二液型ポリウレタンラミネート接着剤の材料系、硬化メカニズム、塗工・ラミネート工程を分解・分析する。',
      },
      {
        text: '車載用硫化物系全固体電池について、材料・界面設計からセル製造に至る主要技術プロセスを再構成する。',
      },
    ],
  },
};

export type FormulationIndustry =
  | 'COSMETICS_PERSONAL_CARE'
  | 'HOUSEHOLD_CHEMICALS'
  | 'BATTERIES'
  | 'PAINT_COATINGS_INKS'
  | 'FOOD_MANUFACTURING'
  | 'SPECIALTY_CHEMICALS';

export const FORMULATION_INDUSTRIES: FormulationIndustry[] = [
  'COSMETICS_PERSONAL_CARE',
  'HOUSEHOLD_CHEMICALS',
  'BATTERIES',
  'PAINT_COATINGS_INKS',
  'FOOD_MANUFACTURING',
  'SPECIALTY_CHEMICALS',
];

export const INDUSTRY_LABELS: Record<Languages, Record<FormulationIndustry, string>> = {
  en: {
    COSMETICS_PERSONAL_CARE: 'Cosmetics &\nPersonal Care',
    HOUSEHOLD_CHEMICALS: 'Household\nChemicals',
    BATTERIES: 'Batteries',
    PAINT_COATINGS_INKS: 'Paint, Coatings\n& Inks',
    FOOD_MANUFACTURING: 'Food\nManufacturing',
    SPECIALTY_CHEMICALS: 'Specialty\nChemicals',
  },
  cn: {
    COSMETICS_PERSONAL_CARE: '化妆品与\n个人护理',
    HOUSEHOLD_CHEMICALS: '家用化学品',
    BATTERIES: '电池',
    PAINT_COATINGS_INKS: '涂料、油墨\n与涂层',
    FOOD_MANUFACTURING: '食品加工',
    SPECIALTY_CHEMICALS: '特殊化学品',
  },
  tw: {
    COSMETICS_PERSONAL_CARE: '化妝品與\n個人護理',
    HOUSEHOLD_CHEMICALS: '家用化學品',
    BATTERIES: '電池',
    PAINT_COATINGS_INKS: '塗料、油墨\n與塗層',
    FOOD_MANUFACTURING: '食品加工',
    SPECIALTY_CHEMICALS: '特種化學品',
  },
  jp: {
    COSMETICS_PERSONAL_CARE: '化粧品・\nパーソナルケア',
    HOUSEHOLD_CHEMICALS: '家庭用化学品',
    BATTERIES: '電池',
    PAINT_COATINGS_INKS: '塗料・インキ\n・コーティング',
    FOOD_MANUFACTURING: '食品製造',
    SPECIALTY_CHEMICALS: '特殊化学品',
  },
};

const FORMULATION_INDUSTRY_PLACEHOLDERS_EN: Record<FormulationIndustry, ExampleItem[]> = {
  COSMETICS_PERSONAL_CARE: [
    {
      text: 'Formulate a lightweight gel-cream moisturizer for oily, acne-prone skin using panthenol as a required soothing agent; target non-comedogenic performance and visible sebum reduction within 4 hours.',
    },
    {
      text: 'Context: I am developing a silicone-free, PEG-free water-in-oil (W/O) long-wear liquid foundation. I need a starting formulation with a specific focus on optimal pigment dispersion for both Titanium Dioxide and Iron Oxides. Strict Formulation Constraints: Emulsifiers: Absolutely no PEG-derivatives or silicone-based emulsifiers (e.g., do not use Cetyl PEG/PPG-10/1 Dimethicone / Abil EM90). Recommend a robust, naturally derived W/O emulsifier. Pigment Treatment: Assume I am purchasing pre-treated hydrophobic pigments. Suggest the best commercial surface treatment (e.g., Triethoxycaprylylsilane, Stearoyl Glutamate) for a volatile alkane oil phase. Do NOT suggest in-situ silane treatments during the bench process. Dispersants: If a secondary dispersant or wetting agent is required, it must be oil-soluble and added to the continuous oil phase. Do not place dispersants in the internal water phase. Required Output: Please provide the full % w/w formula. Then, outline the manufacturing procedure using standard cosmetic processing equipment (e.g., high-shear rotor-stator homogenization or milling). Do NOT suggest microfiltration, as this formula contains suspended particulates.',
    },
    {
      text: 'Develop a ceramide-based cream targeted at dry, compromised skin. The cream should not leave a greasy finish and needs to achieve ≥25% TEWL reduction within 2 h.',
    },
  ],
  HOUSEHOLD_CHEMICALS: [
    {
      text: 'Develop a bio-based multi-surface cleaner that achieves a streak-free finish on glass and stainless steel. Avoid traditional solvents like Butyl Cellosolve; instead, evaluate the use of L-Lactic acid derivatives or Bio-Ethanol. Target a VOC content of $<0.5%.',
    },
    {
      text: 'Develop a solid-state anhydrous toilet bowl cleaner in tablet form. Include a stable oxygen-based bleaching agent',
    },
    {
      text: 'I need to reformulate a standard alkaline degreaser to remove Monoethanolamine (MEA) due to new local safety restrictions. The current formula is 5% MEA, 4% Butyl Glycol, and 2% Nonylphenol Ethoxylate. Please replace the MEA with a less volatile organic base like Triethanolamine or an aminomethyl propanol, replace the NPE with a greener Alcohol Ethoxylate, and adjust the hydrotrope levels (SXS) to keep the cloud point above 50°C.',
    },
  ],
  BATTERIES: [
    {
      text: 'I am looking for a structural adhesive for cell-to-pack bonding in EV batteries. It must achieve a thermal conductivity of >2.0 W/m·K using non-abrasive fillers and possess a dielectric strength of >15 kV/mm. It must preferably maintain a thixotropic index suitable for high-speed robotic bead application without slumping on vertical surfaces.',
    },
    {
      text: 'Design a recipe for a non-flammable gel-polymer electrolyte integrated with a ceramic-coated shutdown separator. The formulation must replace standard volatile carbonates with a mixture containing a phosphorus-based flame retardant and a thermally-triggered shutdown additive. Ensure the battery self-extinguishes upon reaching a critical temperature of 130 °C.',
    },
    {
      text: 'Develop a formulation for a sulfide solid electrolyte thin film, referring to relevant literature as much as possible.',
    },
  ],
  PAINT_COATINGS_INKS: [
    {
      text: 'Get me a solution for solvent-free floor-coating that recovers leveling and surface finish without causing settling or lower slip resistance.',
    },
    {
      text: 'Design a high-performance Conductive Graphene-Polymer Composite Ink with the specific inputs: Active Filler: 18 wt% Functionalized Graphene Oxide (FGO) - High surface area, loaded with -COOH and -OH groups. Binder: Polypropylene (PP) Resin - A strictly non-polar, hydrophobic thermoplastic. Solvent: Terpineol. The final dried film must be homogeneous with no visible particle agglomeration and maximum loading of FGO is required for conductivity',
    },
    {
      text: 'Propose a formulation for an aqueous anionic polyurethane dispersion (PUD) for leather coatings that achieves Martindale abrasion resistance >50,000 cycles, tensile strength >25 MPa, and volatile organic compound (VOC) content <50 g/L.',
    },
  ],
  FOOD_MANUFACTURING: [
    {
      text: 'A formula to reduce bitterness in tea beverages: Reduces bitterness by 30%, while maintaining a rich tea aroma and a smooth, non-gritty texture.',
    },
    {
      text: 'Develop a preliminary formulation for a high-protein (20g per serving) plant-based yogurt. The target texture should mimic a 5% milkfat Greek yogurt. Use pea protein isolate as the primary source but suggest a secondary protein to mask the earthy notes.',
    },
    {
      text: 'Develop an organic preservative that ensures at least 24 months shelf-stability for dry fruit snacks',
    },
  ],
  SPECIALTY_CHEMICALS: [
    {
      text: 'Provide a processing protocol which produces a >85% yield of 130 VI or higher Group III base oil from a waxy hydrocracker waste feedstock. Include reactor temperature profiles, catalyst selection, and recycle-to-feed ratios in your response.',
    },
    {
      text: 'I want to develop an oily boehmite nanosol (20% solids content) with good stability, using acidic boehmite nanorod powder (around 100 nm) as the raw material, with the addition of appropriate modifying agents, and using organic solvents such as ethanol.',
    },
    {
      text: 'I need to reformulate a standard alkaline degreaser to remove Monoethanolamine (MEA) due to new local safety restrictions. The current formula is 5% MEA, 4% Butyl Glycol, and 2% Nonylphenol Ethoxylate. Please replace the MEA with a less volatile organic base like Triethanolamine or an aminomethyl propanol, replace the NPE with a greener Alcohol Ethoxylate, and adjust the hydrotrope levels (SXS) to keep the cloud point above 50°C.',
    },
  ],
};

const FORMULATION_INDUSTRY_PLACEHOLDERS_CN: Record<FormulationIndustry, ExampleItem[]> = {
  COSMETICS_PERSONAL_CARE: [
    {
      text: '配制一款适用于油性、易长痘肌肤的轻盈啫喱面霜保湿产品，需使用泛醇作为必要的舒缓成分；目标为非致粉刺配方，并在4小时内实现可见的皮脂减少效果。',
    },
    {
      text: '背景：我正在开发一款不含硅油、不含 PEG 的油包水 (W/O) 持久粉底液。我需要一个初始配方，重点关注二氧化钛和氧化铁的最佳颜料分散。严格配方限制：乳化剂：绝对不使用 PEG 衍生物或硅基乳化剂（例如，不要使用 Cetyl PEG/PPG-10/1 Dimethicone / Abil EM90）。请推荐一种稳定的天然来源 W/O 乳化剂。颜料处理：假设我购买的是预处理的疏水性颜料。请建议适用于挥发性烷烃油相的最佳商业表面处理方式（例如，三乙氧基辛基硅烷、硬脂酰谷氨酸）。请勿建议在实验室工艺中进行原位硅烷处理。分散剂：如需二次分散剂或润湿剂，必须是油溶性的，并添加到连续油相中。请勿将分散剂置于内部水相中。所需输出：请提供完整的 % w/w 配方。然后，使用标准化妆品加工设备（例如，高剪切转子-定子均质或研磨）概述生产工艺。请勿建议微滤，因为此配方含有悬浮颗粒。',
    },
    {
      text: '开发一款针对干燥、受损肌肤的神经酰胺面霜。面霜使用后不应有油腻感，并需在2小时内使经皮水分流失（TEWL）降低≥25%。',
    },
  ],
  HOUSEHOLD_CHEMICALS: [
    {
      text: '开发一款生物基多功能表面清洁剂，能在玻璃和不锈钢表面实现无痕效果。避免使用丁基溶纤剂等传统溶剂，改用 L-乳酸衍生物或生物乙醇。目标 VOC 含量 <0.5%。',
    },
    {
      text: '开发一款片剂形式的固态无水马桶清洁剂，需含有稳定的氧系漂白剂。',
    },
    {
      text: '由于当地新的安全法规限制，我需要重新配制一款标准碱性脱脂剂以去除单乙醇胺 (MEA)。当前配方为 5% MEA、4% 丁基乙二醇和 2% 壬基酚聚氧乙烯醚。请用挥发性较低的有机碱（如三乙醇胺或氨甲基丙醇）替代 MEA，用更环保的脂肪醇聚氧乙烯醚替代 NPE，并调整助溶剂 (SXS) 用量以保持浊点高于 50°C。',
    },
  ],
  BATTERIES: [
    {
      text: '我正在寻找一种用于电动汽车电池 Cell-to-Pack 粘接的结构胶。要求使用非磨蚀性填料实现导热系数 >2.0 W/m·K，介电强度 >15 kV/mm，并且最好具有适合高速机器人涂胶的触变指数，在垂直表面不流挂。',
    },
    {
      text: '设计一款与陶瓷涂层关断隔膜集成的不可燃凝胶聚合物电解质配方。该配方需用含磷系阻燃剂和热触发关断添加剂的混合物替代标准挥发性碳酸酯。确保电池在达到 130°C 临界温度时能自熄。',
    },
    {
      text: '开发一款硫化物固态电解质薄膜配方，请尽可能参考相关文献。',
    },
  ],
  PAINT_COATINGS_INKS: [
    {
      text: '为我提供一种无溶剂地坪涂料解决方案，能够恢复流平性和表面光洁度，同时不会导致沉降或降低防滑性。',
    },
    {
      text: '设计一款高性能导电石墨烯-聚合物复合油墨，具体参数如下：活性填料：18 wt% 功能化氧化石墨烯 (FGO) - 高比表面积，负载 -COOH 和 -OH 基团。粘结剂：聚丙烯 (PP) 树脂 - 严格非极性的疏水热塑性塑料。溶剂：松油醇。最终干燥薄膜必须均匀，无可见颗粒团聚，并要求最大化 FGO 负载量以提高导电性。',
    },
    {
      text: '提出一款用于皮革涂层的水性阴离子聚氨酯分散体 (PUD) 配方，要求马丁代尔耐磨性 >50,000 次、拉伸强度 >25 MPa、挥发性有机化合物 (VOC) 含量 <50 g/L。',
    },
  ],
  FOOD_MANUFACTURING: [
    {
      text: '一款降低茶饮料苦味的配方：苦味降低 30%，同时保持浓郁的茶香和顺滑、无颗粒感的口感。',
    },
    {
      text: '开发一款高蛋白（每份 20g）植物基酸奶的初步配方。目标质地应模拟 5% 乳脂希腊酸奶。使用豌豆分离蛋白作为主要来源，但建议添加第二种蛋白质以掩盖土腥味。',
    },
    {
      text: '开发一款有机防腐剂，确保干果零食至少 24 个月的货架稳定性。',
    },
  ],
  SPECIALTY_CHEMICALS: [
    {
      text: '提供一套加工工艺方案，从蜡质加氢裂化废料原料中生产粘度指数 130 或更高的 III 类基础油，收率 >85%。请在回复中包含反应器温度曲线、催化剂选择和循环比。',
    },
    {
      text: '我想开发一款油性勃姆石纳米溶胶（固含量 20%），具有良好的稳定性，以酸性勃姆石纳米棒粉末（约 100 nm）为原料，添加适当的改性剂，使用乙醇等有机溶剂。',
    },
    {
      text: '由于当地新的安全法规限制，我需要重新配制一款标准碱性脱脂剂以去除单乙醇胺 (MEA)。当前配方为 5% MEA、4% 丁基乙二醇和 2% 壬基酚聚氧乙烯醚。请用挥发性较低的有机碱（如三乙醇胺或氨甲基丙醇）替代 MEA，用更环保的脂肪醇聚氧乙烯醚替代 NPE，并调整助溶剂 (SXS) 用量以保持浊点高于 50°C。',
    },
  ],
};

const FORMULATION_INDUSTRY_PLACEHOLDERS_TW: Record<FormulationIndustry, ExampleItem[]> = {
  COSMETICS_PERSONAL_CARE: [
    {
      text: '配製一款適用於油性、易長痘肌膚的輕盈啫喱面霜保濕產品，需使用泛醇作為必要的舒緩成分；目標為非致粉刺配方，並在4小時內實現可見的皮脂減少效果。',
    },
    {
      text: '背景：我正在開發一款不含矽油、不含 PEG 的油包水 (W/O) 持久粉底液。我需要一個初始配方，重點關注二氧化鈦和氧化鐵的最佳顏料分散。嚴格配方限制：乳化劑：絕對不使用 PEG 衍生物或矽基乳化劑（例如，不要使用 Cetyl PEG/PPG-10/1 Dimethicone / Abil EM90）。請推薦一種穩定的天然來源 W/O 乳化劑。顏料處理：假設我購買的是預處理的疏水性顏料。請建議適用於揮發性烷烴油相的最佳商業表面處理方式（例如，三乙氧基辛基矽烷、硬脂醯谷氨酸）。請勿建議在實驗室工藝中進行原位矽烷處理。分散劑：如需二次分散劑或潤濕劑，必須是油溶性的，並添加到連續油相中。請勿將分散劑置於內部水相中。所需輸出：請提供完整的 % w/w 配方。然後，使用標準化妝品加工設備（例如，高剪切轉子-定子均質或研磨）概述生產工藝。請勿建議微濾，因為此配方含有懸浮顆粒。',
    },
    {
      text: '開發一款針對乾燥、受損肌膚的神經醯胺面霜。面霜使用後不應有油膩感，並需在2小時內使經皮水分流失（TEWL）降低≥25%。',
    },
  ],
  HOUSEHOLD_CHEMICALS: [
    {
      text: '開發一款生物基多功能表面清潔劑，能在玻璃和不鏽鋼表面實現無痕效果。避免使用丁基溶纖劑等傳統溶劑，改用 L-乳酸衍生物或生物乙醇。目標 VOC 含量 <0.5%。',
    },
    {
      text: '開發一款片劑形式的固態無水馬桶清潔劑，需含有穩定的氧系漂白劑。',
    },
    {
      text: '由於當地新的安全法規限制，我需要重新配製一款標準鹼性脫脂劑以去除單乙醇胺 (MEA)。當前配方為 5% MEA、4% 丁基乙二醇和 2% 壬基酚聚氧乙烯醚。請用揮發性較低的有機鹼（如三乙醇胺或氨甲基丙醇）替代 MEA，用更環保的脂肪醇聚氧乙烯醚替代 NPE，並調整助溶劑 (SXS) 用量以保持濁點高於 50°C。',
    },
  ],
  BATTERIES: [
    {
      text: '我正在尋找一種用於電動汽車電池 Cell-to-Pack 黏接的結構膠。要求使用非磨蝕性填料實現導熱係數 >2.0 W/m·K，介電強度 >15 kV/mm，並且最好具有適合高速機器人塗膠的觸變指數，在垂直表面不流掛。',
    },
    {
      text: '設計一款與陶瓷塗層關斷隔膜整合的不可燃凝膠聚合物電解質配方。該配方需用含磷系阻燃劑和熱觸發關斷添加劑的混合物替代標準揮發性碳酸酯。確保電池在達到 130°C 臨界溫度時能自熄。',
    },
    {
      text: '開發一款硫化物固態電解質薄膜配方，請盡可能參考相關文獻。',
    },
  ],
  PAINT_COATINGS_INKS: [
    {
      text: '為我提供一種無溶劑地坪塗料解決方案，能夠恢復流平性和表面光潔度，同時不會導致沉降或降低防滑性。',
    },
    {
      text: '設計一款高性能導電石墨烯-聚合物複合油墨，具體參數如下：活性填料：18 wt% 功能化氧化石墨烯 (FGO) - 高比表面積，負載 -COOH 和 -OH 基團。黏結劑：聚丙烯 (PP) 樹脂 - 嚴格非極性的疏水熱塑性塑膠。溶劑：松油醇。最終乾燥薄膜必須均勻，無可見顆粒團聚，並要求最大化 FGO 負載量以提高導電性。',
    },
    {
      text: '提出一款用於皮革塗層的水性陰離子聚氨酯分散體 (PUD) 配方，要求馬丁代爾耐磨性 >50,000 次、拉伸強度 >25 MPa、揮發性有機化合物 (VOC) 含量 <50 g/L。',
    },
  ],
  FOOD_MANUFACTURING: [
    {
      text: '一款降低茶飲料苦味的配方：苦味降低 30%，同時保持濃郁的茶香和順滑、無顆粒感的口感。',
    },
    {
      text: '開發一款高蛋白（每份 20g）植物基優格的初步配方。目標質地應模擬 5% 乳脂希臘優格。使用豌豆分離蛋白作為主要來源，但建議添加第二種蛋白質以掩蓋土腥味。',
    },
    {
      text: '開發一款有機防腐劑，確保乾果零食至少 24 個月的貨架穩定性。',
    },
  ],
  SPECIALTY_CHEMICALS: [
    {
      text: '提供一套加工工藝方案，從蠟質加氫裂化廢料原料中生產黏度指數 130 或更高的 III 類基礎油，收率 >85%。請在回覆中包含反應器溫度曲線、催化劑選擇和循環比。',
    },
    {
      text: '我想開發一款油性勃姆石奈米溶膠（固含量 20%），具有良好的穩定性，以酸性勃姆石奈米棒粉末（約 100 nm）為原料，添加適當的改性劑，使用乙醇等有機溶劑。',
    },
    {
      text: '由於當地新的安全法規限制，我需要重新配製一款標準鹼性脫脂劑以去除單乙醇胺 (MEA)。當前配方為 5% MEA、4% 丁基乙二醇和 2% 壬基���聚氧乙烯醚。請用揮發性較低的有機鹼（如三乙醇胺或氨甲基丙醇）替代 MEA，用更環保的脂肪醇聚氧乙烯醚替代 NPE，並調整助溶劑 (SXS) 用量以保持濁點高於 50°C。',
    },
  ],
};

const FORMULATION_INDUSTRY_PLACEHOLDERS_JP: Record<FormulationIndustry, ExampleItem[]> = {
  COSMETICS_PERSONAL_CARE: [
    {
      text: '脂性肌・ニキビができやすい肌向けの軽いジェルクリーム保湿剤を処方する。パンテノールを必須の鎮静成分として使用し、ノンコメドジェニック性能を目標とし、4時間以内に目に見える皮脂減少を実現すること。',
    },
    {
      text: '背景：シリコンフリー、PEGフリーの油中水型（W/O）ロングウェアリキッドファンデーションを開発中です。二酸化チタンと酸化鉄の両方の最適な顔料分散に特に焦点を当てた初期処方が必要です。厳格な処方制約：乳化剤：PEG誘導体やシリコン系乳化剤は絶対に使用しないこと（例：Cetyl PEG/PPG-10/1 Dimethicone / Abil EM90は使用不可）。堅牢な天然由来W/O乳化剤を推奨すること。顔料処理：前処理済みの疎水性顔料を購入する前提とする。揮発性アルカン油相に最適な商業用表面処理（例：トリエトキシカプリリルシラン、ステアロイルグルタミン酸）を提案すること。ベンチプロセス中のin-situシラン処理は提案しないこと。分散剤：二次分散剤または湿潤剤が必要な場合は、油溶性で連続油相に添加すること。内部水相に分散剤を配置しないこと。必要な出力：完全な% w/w処方を提供すること。次に、標準的な化粧品加工機器（例：高せん断ローター・ステーター均質化または粉砕）を使用した製造手順を概説すること。この処方には懸濁粒子が含まれるため、マイクロフィルトレーションは提案しないこと。',
    },
    {
      text: '乾燥し、バリア機能が低下した肌向けのセラミド配合クリームを開発する。べたつきのない仕上がりとし、2時間以内にTEWL（経表皮水分蒸散量）を25％以上低減すること。',
    },
  ],
  HOUSEHOLD_CHEMICALS: [
    {
      text: 'ガラスやステンレス表面で拭き跡が残らないバイオベースの多目的クリーナーを開発する。ブチルセロソルブなどの従来溶剤を避け、L-乳酸誘導体やバイオエタノールの使用を検討する。目標VOC含有量は0.5%未満。',
    },
    {
      text: 'タブレット形状の固形無水トイレ用洗浄剤を開発する。安定した酸素系漂白剤を配合すること。',
    },
    {
      text: '新たな地域安全規制により、標準的なアルカリ脱脂剤からモノエタノールアミン（MEA）を除去する必要がある。現行処方は5% MEA、4%ブチルグリコール、2%ノニルフェノールエトキシレート。MEAをトリエタノールアミンやアミノメチルプロパノールなどの低揮発性有機塩基に置換し、NPEをより環境に優しいアルコールエトキシレートに置換し、曇点を50°C以上に維持するようハイドロトロープ（SXS）レベルを調整すること。',
    },
  ],
  BATTERIES: [
    {
      text: 'EV電池のセル・トゥ・パック接合用構造用接着剤を探している。非研磨性フィラーを使用して熱伝導率>2.0 W/m·K、絶縁破壊強度>15 kV/mmを達成し、垂直面でも垂れない高速ロボット塗布に適したチキソトロピー指数を持つことが望ましい。',
    },
    {
      text: 'セラミックコーティングシャットダウンセパレータと一体化した不燃性ゲルポリマー電解質の処方を設計する。標準的な揮発性カーボネートをリン系難燃剤と熱トリガーシャットダウン添加剤を含む混合物に置換する。臨界温度130°Cに達した際に電池が自己消火することを確保する。',
    },
    {
      text: '硫化物固体電解質薄膜の処方を開発する。関連文献をできる限り参照すること。',
    },
  ],
  PAINT_COATINGS_INKS: [
    {
      text: '沈降や防滑性低下を起こさずにレベリング性と表面仕上げを回復できる無溶剤床用塗料のソリューションを提案する。',
    },
    {
      text: '以下の仕様で高性能導電性グラフェン-ポリマー複合インクを設計する：活性フィラー：18 wt%官能化酸化グラフェン（FGO）- 高比表面積、-COOHおよび-OH基を担持。バインダー：ポリプロピレン（PP）樹脂 - 厳密に非極性の疎水性熱可塑性樹脂。溶剤：テルピネオール。最終乾燥膜は均一で目視可能な粒子凝集がなく、導電性向上のためFGO充填量を最大化すること。',
    },
    {
      text: '皮革コーティング用水性アニオン性ポリウレタンディスパージョン（PUD）の処方を提案する。マーチンデール耐摩耗性>50,000サイクル、引張強度>25 MPa、揮発性有機化合物（VOC）含有量<50 g/Lを達成すること。',
    },
  ],
  FOOD_MANUFACTURING: [
    {
      text: '茶飲料の苦味を低減する処方：苦味を30%低減しながら、豊かな茶の香りと滑らかでざらつきのない食感を維持する。',
    },
    {
      text: '高タンパク（1食あたり20g）植物性ヨーグルトの予備処方を開発する。目標食感は乳脂肪5%のギリシャヨーグルトを模倣する。エンドウ豆分離タンパクを主原料とし、土臭さを隠すための二次タンパク質を提案すること。',
    },
    {
      text: 'ドライフルーツスナックの少なくとも24ヶ月の棚持ち安定性を確保する有機保存料を開発する。',
    },
  ],
  SPECIALTY_CHEMICALS: [
    {
      text: 'ワックス質水素化分解廃棄物原料から粘度指数130以上のグループIII基油を収率85%以上で製造する加工プロトコルを提供する。反応器温度プロファイル、触媒選定、リサイクル比を回答に含めること。',
    },
    {
      text: '酸性ベーマイトナノロッド粉末（約100 nm）を原料とし、適切な改質剤を添加し、エタノールなどの有機溶剤を使用して、安定性の良い油性ベーマイトナノゾル（固形分20%）を開発したい。',
    },
    {
      text: '新たな地域安全規制により、標準的なアルカリ脱脂剤からモノエタノールアミン（MEA）を除去する必要がある。現行処方は5% MEA、4%ブチルグリコール、2%ノニルフェノールエトキシレート。MEAをトリエタノールアミンやアミノメチルプロパノールなどの低揮発性有機塩基に置換し、NPEをより環境に優しいアルコールエトキシレートに置換し、曇点を50°C以上に維持するようハイドロトロープ（SXS）レベルを調整すること。',
    },
  ],
};

export const INDUSTRY_EXAMPLES: Record<Languages, Record<FormulationIndustry, ExampleItem[]>> = {
  en: {
    ...FORMULATION_INDUSTRY_PLACEHOLDERS_EN,
  },
  cn: {
    ...FORMULATION_INDUSTRY_PLACEHOLDERS_CN,
  },
  tw: {
    ...FORMULATION_INDUSTRY_PLACEHOLDERS_TW,
  },
  jp: {
    ...FORMULATION_INDUSTRY_PLACEHOLDERS_JP,
  },
};

export const FILE_EXAMPLES: Partial<
  Record<languages, Partial<Record<MseProduct, FileExampleItem[]>>>
> = {
  en: {},
  cn: {},
  tw: {},
  jp: {},
};
